import { cn } from '#/utils/cn';

import { Icon } from './icons';

const EASTERN_TIME_ZONE = 'America/New_York';

export interface UpdatedProps {
  // Expects ISO 8601 string
  date: string;
  variant: 'time' | 'date';
  showIcon?: boolean;
  className?: string;
}

const estFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: EASTERN_TIME_ZONE,
  timeZoneName: 'short',
});

function formatAsTime(d: Date): string {
  const parts = estFormatter.formatToParts(d);
  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;
  const timeZoneName = parts.find(
    (part) => part.type === 'timeZoneName',
  )?.value;

  return `${hour}:${minute} ${timeZoneName}`;
}

function formatAsDate(d: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export function Updated({
  date,
  variant,
  showIcon = true,
  className,
}: UpdatedProps) {
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const formatted =
    variant === 'time' ? formatAsTime(parsed) : formatAsDate(parsed);
  const label = variant === 'time' ? 'Last updated at' : 'Last updated';
  const badgeClassName = showIcon
    ? 'grid-cols-[auto_minmax(0,1fr)] gap-4'
    : 'grid-cols-[minmax(0,1fr)]';

  return (
    <p className={cn('@container w-full min-w-0', className)}>
      <span
        className={cn(
          'text-static-12 text-fg bg-slate-10 inline-grid max-w-full min-w-0 items-center rounded px-8 py-2',
          badgeClassName,
        )}
      >
        {showIcon ? (
          <span className="shrink-0 @max-[176px]:self-start">
            <Icon id="timestamp" size={16} aria-hidden="true" />
          </span>
        ) : null}
        <span className="flex min-w-0 items-baseline gap-2 @max-[176px]:flex-col @max-[176px]:items-start @max-[176px]:gap-1">
          <span className="whitespace-nowrap">{label}</span>
          <time className="whitespace-nowrap" dateTime={date}>
            {formatted}
          </time>
        </span>
      </span>
    </p>
  );
}
